import React from "react";
import { Link } from "react-router-dom";
import { Target, Eye, ShieldCheck, ShoppingBag, Store, Users, GraduationCap, Radio, Gift, Sparkles, ArrowRight, Heart, Mail } from "lucide-react";
import { PageHero } from "../components/FormKit";
import { useContent } from "../hooks/useContent";

const ABOUT_FALLBACK = {
  eyebrow: "About TAVEN SYSTEM LLC",
  title: "More Than a Store — an Ecosystem",
  subtitle: "TAVEN SYSTEM LLC is a digital commerce ecosystem built to help people shop, learn, sell, earn, and grow. We connect online shopping, seller opportunities, affiliate business, education, rewards, live commerce, and digital tools in one platform.",
  badge: "Build. Earn. Grow. Leave a Legacy.",
  mission_title: "Our Mission",
  mission_text: "To empower everyday people to build income and independence through commerce and education — giving them the products, opportunities, and knowledge to shop smarter, sell confidently, and grow a lasting legacy.",
  vision_title: "Our Vision",
  vision_text: "To become a trusted global ecosystem where shopping, learning, and earning come together — a single platform where anyone, anywhere, can turn ambition into a real business.",
  trust_title: "Your Trust Is Our Foundation",
  trust_text: "Every order is protected, every price is checked for fairness, and every question is answered by real people who care. We are committed to a safe, transparent, and premium experience for every member of the TAVEN community.",
};

const VALUES = [
  { icon: ShieldCheck, title: "Trust & Transparency", text: "Fair prices, honest listings, and clear communication at every step." },
  { icon: Heart, title: "People First", text: "We build tools that help real people shop, earn, learn, and grow." },
  { icon: Sparkles, title: "Excellence", text: "A premium experience — from product quality to customer care." },
  { icon: Users, title: "Community & Legacy", text: "We help members build something that lasts beyond a single sale." },
];

const OFFERS = [
  { icon: ShoppingBag, title: "Online Shopping", text: "A curated marketplace of quality products with secure checkout and buyer protection." },
  { icon: Store, title: "Seller Opportunity", text: "Launch or bring your shop to TAVEN and reach new customers with powerful tools." },
  { icon: Users, title: "Affiliate Business", text: "Earn by sharing what you love through our transparent affiliate program." },
  { icon: GraduationCap, title: "Learning Center", text: "The TAVEN Academy — real courses on commerce, marketing, and leadership with official certificates." },
  { icon: Radio, title: "Live Commerce", text: "Modern, engaging live shopping where hosts showcase products in real time." },
  { icon: Gift, title: "Rewards & Coins", text: "Earn TAVEN Coins, spin for rewards, and redeem exclusive member benefits." },
];

export const AboutUs = () => {
  const c = useContent("about_page", ABOUT_FALLBACK);
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow={c.eyebrow}
        title={c.title}
        subtitle={c.subtitle}
      >
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] bg-taven-gold text-black px-3 py-1.5">{c.badge}</span>
        </div>
      </PageHero>

      {/* Mission & Vision */}
      <section className="py-14 px-4 md:px-8 lg:px-12 mx-auto max-w-7xl">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="border border-black/10 bg-white p-8" data-testid="about-mission">
            <div className="w-11 h-11 bg-taven-black text-taven-gold flex items-center justify-center"><Target className="w-6 h-6" /></div>
            <h2 className="text-lg md:text-lg font-black font-display uppercase tracking-wide mt-4">{c.mission_title}</h2>
            <p className="text-base text-muted-foreground mt-3 leading-relaxed">{c.mission_text}</p>
          </div>
          <div className="border border-black/10 bg-white p-8" data-testid="about-vision">
            <div className="w-11 h-11 bg-taven-black text-taven-gold flex items-center justify-center"><Eye className="w-6 h-6" /></div>
            <h2 className="text-lg md:text-lg font-black font-display uppercase tracking-wide mt-4">{c.vision_title}</h2>
            <p className="text-base text-muted-foreground mt-3 leading-relaxed">{c.vision_text}</p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-14 px-4 md:px-8 lg:px-12 bg-taven-black text-white">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center gap-2 text-taven-gold"><Sparkles className="w-4 h-4" /><span className="text-xs font-bold uppercase tracking-[0.2em]">What We Stand For</span></div>
          <h2 className="text-3xl sm:text-4xl font-black font-display mt-2 mb-10">Our Values</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="about-values">
            {VALUES.map((v) => (
              <div key={v.title} className="border border-white/10 bg-white/[0.03] p-6 hover:border-taven-gold transition-colors">
                <v.icon className="w-7 h-7 text-taven-gold" />
                <p className="font-black font-display mt-4">{v.title}</p>
                <p className="text-sm text-white/60 mt-2 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What TAVEN Offers */}
      <section className="py-14 px-4 md:px-8 lg:px-12 mx-auto max-w-7xl">
        <div className="flex items-center gap-2 text-taven-gold"><ShoppingBag className="w-4 h-4" /><span className="text-xs font-bold uppercase tracking-[0.2em]">The Ecosystem</span></div>
        <h2 className="text-3xl sm:text-4xl font-black font-display mt-2 mb-10">What TAVEN Offers</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="about-offers">
          {OFFERS.map((o) => (
            <div key={o.title} className="border border-black/10 bg-white p-6 hover:border-taven-gold hover-lift group">
              <div className="w-11 h-11 bg-taven-gold/10 text-taven-black flex items-center justify-center group-hover:bg-taven-gold transition-colors"><o.icon className="w-6 h-6" /></div>
              <p className="font-black font-display mt-4 group-hover:text-taven-gold">{o.title}</p>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{o.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Opportunity CTAs */}
      <section className="py-14 px-4 md:px-8 lg:px-12 bg-taven-gray">
        <div className="mx-auto max-w-7xl grid md:grid-cols-3 gap-4">
          <div className="border border-black/10 bg-white p-8" data-testid="about-seller">
            <Store className="w-8 h-8 text-taven-gold" />
            <h3 className="text-lg font-black font-display mt-4">Seller Opportunity</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Turn products into a business. Sell on TAVEN or bring your existing shop and reach a growing audience with premium tools and support.</p>
            <Link to="/become-a-seller" data-testid="about-seller-cta" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-taven-black mt-5 hover:text-taven-gold">Become a Seller <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="border border-black/10 bg-white p-8" data-testid="about-affiliate">
            <Users className="w-8 h-8 text-taven-gold" />
            <h3 className="text-lg font-black font-display mt-4">Affiliate Opportunity</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Earn by recommending products you believe in. Our affiliate program is transparent, fair, and built for real, sustainable income.</p>
            <Link to="/affiliate-program" data-testid="about-affiliate-cta" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-taven-black mt-5 hover:text-taven-gold">Join the Program <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="border border-black/10 bg-white p-8" data-testid="about-learning">
            <GraduationCap className="w-8 h-8 text-taven-gold" />
            <h3 className="text-lg font-black font-display mt-4">Learning Center</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">The TAVEN Academy teaches you how to succeed — real courses, guided instructors, and official certificates in four languages.</p>
            <Link to="/education" data-testid="about-learning-cta" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-taven-black mt-5 hover:text-taven-gold">Explore Courses <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* Trust + Contact */}
      <section className="py-16 px-4 md:px-8 lg:px-12 bg-taven-black text-white">
        <div className="mx-auto max-w-3xl text-center">
          <ShieldCheck className="w-10 h-10 text-taven-gold mx-auto" />
          <h2 className="text-2xl sm:text-3xl font-black font-display mt-4">{c.trust_title}</h2>
          <p className="text-base text-white/60 mt-4 leading-relaxed">{c.trust_text}</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/contact" data-testid="about-contact-cta" className="inline-flex items-center gap-2 bg-taven-gold text-black font-bold uppercase tracking-wider text-xs px-5 py-3 hover:bg-white transition-colors"><Mail className="w-4 h-4" /> Contact Support</Link>
            <Link to="/help" data-testid="about-help-cta" className="inline-flex items-center gap-2 border border-white/25 text-white font-bold uppercase tracking-wider text-xs px-5 py-3 hover:border-taven-gold transition-colors">Visit Help Center</Link>
          </div>
          <p className="text-xs text-white/40 mt-6">TAVEN SYSTEM LLC · support@taven.store</p>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
